'use client'

import Image from 'next/image'
import { useEffect, useRef, useState } from 'react'

import { useVizVisible } from '../Viz'

import './wafflr.css'

type Mode = 'old' | 'now'

// Times are in days from Monday 00:00, 0 to 7.
const WEEK = 7
// Demo days per real second.
const SPEED = 1.1
const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
const MEMBERS = [
  { icon: 'chef', at: 2.4 }, // wednesday morning
  { icon: 'monocle', at: 3.1 }, // wednesday night
  { icon: 'wizard', at: 4.3 }, // thursday evening: late under the 2025 rules
  { icon: 'shades', at: null }, // never posts
] as const
// Under the current rules everyone posts around the picked day; one a day late.
const NOW_OFFSETS = [0.2, 0.55, 1.15]

function postingWindow(mode: Mode, picked: number): [number, number] {
  return mode === 'old' ? [2, 3.5] : [picked, picked + 1]
}

function postTime(mode: Mode, picked: number, i: number) {
  const at = MEMBERS[i]!.at
  if (at === null) return null
  if (mode === 'old') return at
  return Math.min(6.92, picked + NOW_OFFSETS[i]!)
}

const pct = (days: number) => `${(days / WEEK) * 100}%`

// One Stack's week, played out under the 2025 rules (a 36-hour Wednesday
// window and a Late Waffle tag) or the current ones (the Stack picks a day).
export function WeekDemo() {
  const visible = useVizVisible()
  const [mode, setMode] = useState<Mode>('old')
  const [picked, setPicked] = useState(4)
  const [t, setT] = useState(0)
  const [run, setRun] = useState(0)
  const tRef = useRef(0)

  // Plays the week forward while the demo is on screen. Leaving pauses it;
  // coming back resumes unless the week already finished.
  useEffect(() => {
    if (!visible || tRef.current >= WEEK) return
    let frame = 0
    let last = performance.now()
    const step = (now: number) => {
      tRef.current = Math.min(
        WEEK,
        tRef.current + ((now - last) / 1000) * SPEED,
      )
      last = now
      setT(tRef.current)
      if (tRef.current < WEEK) frame = requestAnimationFrame(step)
    }
    frame = requestAnimationFrame(step)
    return () => cancelAnimationFrame(frame)
  }, [visible, run])

  const restart = () => {
    tRef.current = 0
    setT(0)
    setRun((r) => r + 1)
  }

  const chooseMode = (next: Mode) => {
    setMode(next)
    restart()
  }

  const pickDay = (day: number) => {
    setPicked(day)
    restart()
  }

  const [start, end] = postingWindow(mode, picked)
  let posted = 0
  const rows = MEMBERS.map((member, i) => {
    const at = postTime(mode, picked, i)
    const on = at !== null && t >= at
    if (on) posted++
    return {
      ...member,
      at,
      on,
      late: mode === 'old' && at !== null && at > end,
    }
  })

  return (
    <div className="wf-week" data-mode={mode}>
      <div className="wf-week-top">
        <div className="wf-seg" role="group" aria-label="Rules">
          <button
            type="button"
            aria-pressed={mode === 'old'}
            onClick={() => chooseMode('old')}
          >
            2025 rules
          </button>
          <button
            type="button"
            aria-pressed={mode === 'now'}
            onClick={() => chooseMode('now')}
          >
            Now
          </button>
        </div>
        <span className="wf-hint">
          {mode === 'old'
            ? 'the wednesday window: 36 hours'
            : "your stack's day. tap another one"}
        </span>
      </div>

      <div className="wf-days">
        <span />
        {DAYS.map((day, i) => (
          <button
            key={day}
            type="button"
            className="wf-day"
            data-press="none"
            disabled={mode === 'old'}
            aria-pressed={mode === 'now' ? i === picked : undefined}
            onClick={() => pickDay(i)}
          >
            {day}
          </button>
        ))}
      </div>

      <div className="wf-track">
        <div className="wf-overlay" aria-hidden="true">
          <div
            className="wf-band"
            style={{
              left: pct(start),
              width: pct(Math.min(end, WEEK) - start),
            }}
          />
          <div className="wf-playhead" style={{ left: pct(t) }} />
        </div>
        {rows.map((row) => (
          <div key={row.icon} className="wf-row">
            <Image
              src={`/images/projects/wafflr/story/icon-${row.icon}.webp`}
              alt=""
              width={34}
              height={34}
              unoptimized
            />
            <div className="wf-lane">
              {row.at !== null ? (
                <div
                  className="wf-post"
                  data-on={row.on || undefined}
                  style={{
                    left: pct(row.at),
                    backgroundImage: `url(/images/projects/wafflr/story/icon-${row.icon}.webp)`,
                  }}
                >
                  {row.late && <span className="wf-tag">Late Waffle 🧇</span>}
                </div>
              ) : (
                <span className="wf-strike" data-on={t >= 6.95 || undefined}>
                  {mode === 'old' ? 'strike 2 of 3' : 'no waffle this week'}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="wf-foot">
        <span className="wf-pill" aria-live="polite">
          {posted} of 4 posted
        </span>
        <button type="button" className="wf-replay" onClick={restart}>
          Replay the week
        </button>
      </div>
    </div>
  )
}
